import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiPlus, FiSliders } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { capPoliciesApi, externalCapsApi, memberCapsApi, membersApi, teamsApi } from '../services/api';

const OBJECT_TYPES = ['contacts', 'companies', 'deals', 'tickets', 'leads'];

const EMPTY_SOURCE = {
  object_type: 'contacts',
  owner_property_name: 'hubspot_owner_id',
  execution_mode: 'scheduled',
  sync_frequency_minutes: 15,
  period_mode: 'member_cap_period',
  is_active: true,
};

const newEmptyForm = () => ({
  name: '',
  description: '',
  hs_owner_id: '',
  scope_type: 'global_member',
  hs_team_id: '',
  period_type: 'weekly',
  period_anchor: 'monday',
  criteria_type: 'all',
  segment_property: '',
  segments: [{ id: null, value: '', cap_limit: 10, rules: [], external_sources: [] }],
  usage_source: 'distributely',
  external_source: { ...EMPTY_SOURCE },
  preserve_external_conditions: false,
  is_active: true,
});

const USAGE_SOURCE_LABELS = {
  distributely: 'Distributely assignments',
  combined: 'Distributely + HubSpot workload',
  hubspot: 'HubSpot workload only',
};

const HEALTH_STYLES = {
  healthy: 'bg-green-100 text-green-800',
  live: 'bg-purple-100 text-purple-800',
  stale: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  initial_sync_required: 'bg-blue-100 text-blue-800',
  paused: 'bg-gray-100 text-gray-600',
  needs_attention: 'bg-amber-100 text-amber-800',
};

function ownerLabel(owner) {
  const name = `${owner.firstName || owner.first_name || ''} ${owner.lastName || owner.last_name || ''}`.trim();
  return name || owner.email || owner.name || owner.id || owner.hs_owner_id;
}

function sourceHealthLabel(value) {
  return String(value || 'unknown').replaceAll('_', ' ');
}

function formFromPolicy(policy) {
  const segments = policy.segments?.length ? policy.segments : [{
    id: policy.id,
    value: policy.dimension_value || '',
    cap_limit: policy.cap_limit || 10,
    rules: policy.rules || [],
    external_sources: policy.external_sources || [],
  }];
  const source = policy.external_source || segments.flatMap((segment) => segment.external_sources || [])[0];
  return {
    name: policy.name || '',
    description: policy.description || '',
    hs_owner_id: String(policy.hs_owner_id || ''),
    scope_type: policy.scope_type || 'global_member',
    hs_team_id: String(policy.hs_team_id || ''),
    period_type: policy.period_type || 'weekly',
    period_anchor: policy.period_anchor || 'monday',
    criteria_type: policy.segment_property ? 'segmented' : 'all',
    segment_property: policy.segment_property || '',
    segments: segments.map((segment) => ({
      ...segment,
      value: segment.value || '',
      cap_limit: segment.cap_limit || 10,
      rules: segment.rules || [],
      external_sources: segment.external_sources || [],
    })),
    usage_source: policy.usage_source || 'distributely',
    external_source: { ...EMPTY_SOURCE, ...(source || {}) },
    preserve_external_conditions: policy.criteria_mode === 'legacy_separate',
    is_active: Boolean(policy.is_active),
  };
}

export default function Caps() {
  const { selectedPortal } = useHubSpotPortals();
  const portalId = selectedPortal?.portal_id;
  const [policies, setPolicies] = useState([]);
  const [owners, setOwners] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(newEmptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    if (!portalId) {
      setPolicies([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [policyResult, ownerResult, teamResult] = await Promise.all([
        capPoliciesApi.fetchPolicies(portalId),
        membersApi.fetchHubspotOwners(portalId),
        teamsApi.fetchTeams(portalId),
      ]);
      setPolicies(policyResult?.policies || []);
      setOwners(ownerResult?.owners || ownerResult?.data || ownerResult || []);
      setTeams(teamResult?.teams || []);
    } catch (err) {
      console.error('Failed to load caps:', err);
      setError(err?.response?.data?.message || 'Caps could not be loaded. No partial configuration is being shown.');
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  useEffect(() => { load(); }, [load]);

  const ownerNames = useMemo(() => new Map(owners.map((owner) => [
    String(owner.id || owner.hs_owner_id),
    ownerLabel(owner),
  ])), [owners]);
  const teamNames = useMemo(() => new Map(teams.map((team) => [
    String(team.hs_team_id || team.id),
    team.name || team.custom_team_name || `Team ${team.hs_team_id || team.id}`,
  ])), [teams]);
  const visiblePolicies = policies.filter((policy) => showInactive || policy.is_active);

  const openCreate = () => {
    setEditingId(null);
    setForm(newEmptyForm());
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (policy) => {
    setEditingId(policy.id);
    setForm(formFromPolicy(policy));
    setFormError('');
    setModalOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'scope_type' && value === 'global_member') next.hs_team_id = '';
      if (field === 'period_type') {
        next.period_anchor = {
          daily: 'day_start', weekly: 'monday', monthly: 'month_start', quarterly: 'quarter_start', none: 'none',
        }[value];
      }
      if (field === 'criteria_type' && value === 'all') {
        next.segment_property = '';
        next.segments = [{ ...current.segments[0], value: '' }];
      } else if (field === 'criteria_type' && value === 'segmented') {
        next.segments = current.segments.map((segment) => ({ ...segment, value: segment.value || '' }));
      }
      if (field === 'usage_source' && value !== 'distributely') {
        next.external_source = { ...current.external_source, is_active: true };
      }
      return next;
    });
  };

  const updateSource = (field, value) => {
    setForm((current) => {
      const source = { ...current.external_source, [field]: value };
      if (field === 'execution_mode' && value === 'real_time') source.sync_frequency_minutes = null;
      if (field === 'execution_mode' && value === 'scheduled' && !source.sync_frequency_minutes) {
        source.sync_frequency_minutes = 15;
      }
      return { ...current, external_source: source };
    });
  };

  const updateSegment = (index, field, value) => {
    setForm((current) => ({
      ...current,
      segments: current.segments.map((segment, segmentIndex) =>
        segmentIndex === index ? { ...segment, [field]: value } : segment
      ),
    }));
  };

  const addSegment = () => {
    setForm((current) => ({
      ...current,
      segments: [...current.segments, { id: null, value: '', cap_limit: 10, rules: [], external_sources: [] }],
    }));
  };

  const removeSegment = (index) => {
    setForm((current) => ({ ...current, segments: current.segments.filter((_, segmentIndex) => segmentIndex !== index) }));
  };

  const save = async () => {
    if (!portalId) return;
    if (!form.name.trim() || !form.hs_owner_id) {
      setFormError('Name and member are required.');
      return;
    }
    if (form.scope_type === 'team_member' && !form.hs_team_id) {
      setFormError('Select a team for a team-scoped cap.');
      return;
    }
    if (!form.segments.length || form.segments.some((segment) => !Number.isInteger(Number(segment.cap_limit)) || Number(segment.cap_limit) <= 0)) {
      setFormError('Add at least one limit and use positive whole numbers.');
      return;
    }
    if (form.criteria_type === 'segmented' && (!form.segment_property.trim() || form.segments.some((segment) => !segment.value.trim()))) {
      setFormError('A property and value are required for every condition-specific limit.');
      return;
    }
    const uniqueValues = new Set(form.segments.map((segment) => segment.value.trim().toLowerCase()));
    if (form.criteria_type === 'segmented' && uniqueValues.size !== form.segments.length) {
      setFormError('Each condition value must be unique.');
      return;
    }

    const payload = {
      portal_id: Number(portalId),
      name: form.name,
      description: form.description,
      hs_owner_id: form.hs_owner_id,
      scope_type: form.scope_type,
      hs_team_id: form.scope_type === 'team_member' ? form.hs_team_id : null,
      period_type: form.period_type,
      period_anchor: form.period_anchor,
      usage_source: form.usage_source,
      is_active: form.is_active,
      segment_property: form.criteria_type === 'segmented' ? form.segment_property.trim() : null,
      segments: form.segments.map((segment) => ({
        ...segment,
        value: form.criteria_type === 'segmented' ? segment.value.trim() : null,
        cap_limit: Number(segment.cap_limit),
      })),
      external_source: form.usage_source === 'distributely' ? null : {
        ...form.external_source,
        sync_frequency_minutes: form.external_source.execution_mode === 'real_time'
          ? null : Number(form.external_source.sync_frequency_minutes || 15),
      },
      preserve_external_conditions: form.preserve_external_conditions,
    };
    setSaving(true);
    setFormError('');
    try {
      if (editingId) await capPoliciesApi.updatePolicy(editingId, payload);
      else await capPoliciesApi.createPolicy(payload);
      setModalOpen(false);
      await load();
    } catch (err) {
      console.error('Failed to save cap:', err);
      setFormError(err?.response?.data?.message || err?.response?.data?.error || 'The cap could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (policy) => {
    if (!window.confirm(`Deactivate “${policy.name}”? It will stop affecting routing.`)) return;
    try {
      await capPoliciesApi.deactivatePolicy(policy.id, portalId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'The cap could not be deactivated.');
    }
  };

  const syncPolicy = async (policy) => {
    const sources = (policy.external_sources || []).filter(
      (source) => source.is_active && source.execution_mode === 'scheduled'
    );
    if (!sources.length) return;
    setSyncingId(policy.id);
    setError('');
    try {
      for (const source of sources) await externalCapsApi.syncExternalCapNow(source.id, portalId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'HubSpot workload sync failed.');
    } finally {
      setSyncingId(null);
    }
  };

  const exportCsv = async () => {
    if (!portalId) return;
    try {
      const response = await memberCapsApi.exportMemberCaps(portalId, { includeInactive: showInactive });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `caps-${portalId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Caps could not be exported.');
    }
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !portalId) return;
    setImporting(true);
    setError('');
    try {
      await memberCapsApi.importMemberCaps(portalId, await file.text());
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Caps could not be imported. Check the CSV and try again.');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const primarySource = form.external_source || EMPTY_SOURCE;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-7">
        <div className="flex items-center gap-3 mb-2">
          <FiSliders className="w-6 h-6 text-gray-600" />
          <h1 className="text-2xl font-bold text-gray-900">Caps</h1>
        </div>
        <p className="text-gray-600">Control member workload with one policy, whether usage comes from Distributely, HubSpot, or both.</p>
        {selectedPortal && <p className="text-sm text-blue-700 mt-2">Portal: <span className="font-semibold">{selectedPortal.hub_domain}</span></p>}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          <button onClick={openCreate} disabled={!portalId} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"><FiPlus className="mr-2" /> Create cap</button>
          <button onClick={exportCsv} disabled={!portalId} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50">Export CSV</button>
          <label className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 cursor-pointer">{importing ? 'Importing…' : 'Import CSV'}<input type="file" accept=".csv,text/csv" className="hidden" disabled={importing || !portalId} onChange={importCsv} /></label>
        </div>
        <label className="text-sm text-gray-600"><input type="checkbox" className="mr-2" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />Show inactive</label>
      </div>

      {error && <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">{error}</div>}
      {!portalId && <div className="p-8 bg-white border rounded-lg text-center text-gray-500">Select a portal to manage caps.</div>}
      {portalId && loading && <div className="p-8 bg-white border rounded-lg text-center text-gray-500">Loading complete cap policies…</div>}
      {portalId && !loading && !error && !visiblePolicies.length && <div className="p-8 bg-white border rounded-lg text-center text-gray-500">No caps configured.</div>}

      {portalId && !loading && visiblePolicies.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr><th className="px-4 py-3 text-left">Cap</th><th className="px-4 py-3 text-left">Member</th><th className="px-4 py-3 text-left">Scope</th><th className="px-4 py-3 text-left">Limit</th><th className="px-4 py-3 text-left">Usage source</th><th className="px-4 py-3 text-left">Health</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visiblePolicies.map((policy) => {
                  const sources = policy.external_sources || [];
                  const isExpanded = expanded.has(policy.id);
                  return [
                    <tr key={`policy-${policy.id}`} className={!policy.is_active ? 'opacity-60' : ''}>
                      <td className="px-4 py-3">
                        <button className="inline-flex items-center font-medium text-gray-900" onClick={() => toggleExpanded(policy.id)}>
                          {isExpanded ? <FiChevronDown className="mr-2" /> : <FiChevronRight className="mr-2" />}{policy.name}
                        </button>
                        {!policy.is_active && <span className="ml-2 text-xs text-gray-500">Inactive</span>}
                      </td>
                      <td className="px-4 py-3">{ownerNames.get(String(policy.hs_owner_id)) || policy.hs_owner_id}</td>
                      <td className="px-4 py-3">{policy.scope_type === 'team_member' ? teamNames.get(String(policy.hs_team_id)) || policy.hs_team_id : 'All teams'}</td>
                      <td className="px-4 py-3">{policy.segments?.length > 1 ? `${policy.segments.length} condition limits` : `${policy.cap_limit} / ${policy.period_type === 'none' ? 'never resets' : policy.period_type}`}</td>
                      <td className="px-4 py-3">{USAGE_SOURCE_LABELS[policy.usage_source] || policy.usage_source}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs capitalize ${HEALTH_STYLES[policy.health] || HEALTH_STYLES.paused}`}>{sourceHealthLabel(policy.health)}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {policy.is_active && sources.some((source) => source.is_active && source.execution_mode === 'scheduled') && <button className="text-indigo-600 mr-3" onClick={() => syncPolicy(policy)} disabled={syncingId === policy.id}>{syncingId === policy.id ? 'Syncing…' : 'Sync'}</button>}
                        <button className="text-blue-600 mr-3" onClick={() => openEdit(policy)}>Edit</button>
                        {policy.is_active && <button className="text-red-600" onClick={() => deactivate(policy)}>Deactivate</button>}
                      </td>
                    </tr>,
                    isExpanded && <tr key={`detail-${policy.id}`} className="bg-gray-50"><td colSpan="7" className="px-10 py-4">
                      {policy.diagnostics?.length > 0 && <div className="mb-3 text-amber-800"><FiAlertTriangle className="inline mr-2" />{policy.diagnostics.join(' · ')}</div>}
                      {policy.warnings?.length > 0 && <div className="mb-3 text-blue-800"><FiAlertTriangle className="inline mr-2" />{policy.warnings.join(' · ')}</div>}
                      <div className="text-xs uppercase text-gray-500 font-semibold mb-2">Limits</div>
                      <div className="mb-4 grid gap-1">
                        {(policy.segments || []).map((segment) => <div key={segment.id} className="flex gap-3 text-gray-700">
                          <span className="font-medium">{policy.segment_property ? `${policy.segment_property} = ${segment.value}` : 'All matching records'}</span>
                          <span>{segment.cap_limit} / {policy.period_type === 'none' ? 'never resets' : policy.period_type}</span>
                        </div>)}
                      </div>
                      <div className="text-xs uppercase text-gray-500 font-semibold mb-2">HubSpot sources</div>
                      {!sources.length ? <div className="text-gray-600">No HubSpot source. Usage comes from Distributely counters.</div> : sources.map((source) => <div key={source.id} className="flex flex-wrap items-center gap-3 py-2">
                        <span className="font-medium">{source.object_type}</span><span>{source.execution_mode === 'real_time' ? 'Live lookup' : `Every ${source.sync_frequency_minutes} min`}</span><span>{source.conditions?.length || 0} condition(s)</span><span className={`px-2 py-1 rounded-full text-xs capitalize ${HEALTH_STYLES[source.health] || HEALTH_STYLES.paused}`}>{sourceHealthLabel(source.health)}</span>{source.last_success_at && <span className="text-gray-500">Last success {new Date(source.last_success_at).toLocaleString()}</span>}
                      </div>)}
                    </td></tr>,
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b flex justify-between"><h2 className="text-lg font-semibold">{editingId ? 'Edit cap' : 'Create cap'}</h2><button onClick={() => setModalOpen(false)} disabled={saving}>Close</button></div>
          <div className="p-6 overflow-y-auto space-y-7">
            <section><h3 className="font-semibold mb-3">1. Who is capped?</h3><div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">Name<input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.name} onChange={(event) => updateForm('name', event.target.value)} /></label>
              <label className="text-sm">Member<select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.hs_owner_id} onChange={(event) => updateForm('hs_owner_id', event.target.value)}><option value="">Select member</option>{owners.map((owner) => { const id = owner.id || owner.hs_owner_id; return <option key={id} value={id}>{ownerLabel(owner)}</option>; })}</select></label>
              <label className="text-sm">Scope<select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.scope_type} onChange={(event) => updateForm('scope_type', event.target.value)}><option value="global_member">Across all teams</option><option value="team_member">Only within a team</option></select></label>
              {form.scope_type === 'team_member' && <label className="text-sm">Team<select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.hs_team_id} onChange={(event) => updateForm('hs_team_id', event.target.value)}><option value="">Select team</option>{teams.map((team) => { const id = team.hs_team_id || team.id; return <option key={id} value={id}>{team.name || team.custom_team_name || id}</option>; })}</select></label>}
            </div></section>

            <section><h3 className="font-semibold mb-3">2. When does usage reset?</h3><div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">Reset period<select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.period_type} onChange={(event) => updateForm('period_type', event.target.value)}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="none">Never resets</option></select></label>
              {form.period_type === 'weekly' && <label className="text-sm">Week starts<select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.period_anchor} onChange={(event) => updateForm('period_anchor', event.target.value)}><option value="monday">Monday</option><option value="sunday">Sunday</option></select></label>}
            </div></section>

            <section><h3 className="font-semibold mb-1">3. What are the limits?</h3><p className="text-sm text-gray-500 mb-3">Use one limit for all records, or set different limits for values of one property.</p>
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <label className={`border rounded-lg p-3 cursor-pointer ${form.criteria_type === 'all' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}><input type="radio" className="mr-2" checked={form.criteria_type === 'all'} onChange={() => updateForm('criteria_type', 'all')} />One limit for all records</label>
                <label className={`border rounded-lg p-3 cursor-pointer ${form.criteria_type === 'segmented' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}><input type="radio" className="mr-2" checked={form.criteria_type === 'segmented'} onChange={() => updateForm('criteria_type', 'segmented')} />Different limits by property value</label>
              </div>
              {form.criteria_type === 'segmented' && <label className="block text-sm mb-4 max-w-md">Property<input className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="e.g. city" value={form.segment_property} onChange={(event) => updateForm('segment_property', event.target.value)} /><span className="block text-xs text-gray-500 mt-1">The same property is used for routed records and matching HubSpot records.</span></label>}
              <div className="space-y-2">
                {form.segments.map((segment, index) => <div key={segment.id || `new-${index}`} className={`grid gap-3 items-end ${form.criteria_type === 'segmented' ? 'md:grid-cols-[1fr_180px_auto]' : 'md:grid-cols-[180px]'}`}>
                  {form.criteria_type === 'segmented' && <label className="text-sm">Exact value<input className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="e.g. Durango" value={segment.value} onChange={(event) => updateSegment(index, 'value', event.target.value)} /></label>}
                  <label className="text-sm">Maximum usage<input type="number" min="1" step="1" className="mt-1 w-full border rounded-lg px-3 py-2" value={segment.cap_limit} onChange={(event) => updateSegment(index, 'cap_limit', event.target.value)} /></label>
                  {form.criteria_type === 'segmented' && form.segments.length > 1 && <button type="button" className="text-red-600 px-2 py-2" onClick={() => removeSegment(index)}>Remove</button>}
                </div>)}
              </div>
              {form.criteria_type === 'segmented' && <button type="button" className="text-blue-600 text-sm mt-3" onClick={addSegment}><FiPlus className="inline mr-1" />Add another value</button>}
            </section>

            <section><h3 className="font-semibold mb-3">4. What counts toward usage?</h3><div className="grid md:grid-cols-3 gap-3">
              {Object.entries(USAGE_SOURCE_LABELS).map(([value, label]) => <label key={value} className={`border rounded-lg p-3 cursor-pointer ${form.usage_source === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}><input type="radio" className="mr-2" checked={form.usage_source === value} onChange={() => updateForm('usage_source', value)} />{label}{value === 'combined' && <div className="text-xs text-amber-700 mt-2">Beta: existing all-owned searches may overlap Distributely counters.</div>}</label>)}
            </div>
            {form.usage_source !== 'distributely' && <div className="mt-5 border rounded-lg p-4 bg-gray-50 space-y-4">
              <div className="flex items-center justify-between"><h4 className="font-medium">HubSpot workload source</h4></div>
              <div className="grid md:grid-cols-4 gap-3">
                <label className="text-sm">Object<select className="mt-1 w-full border rounded-lg px-3 py-2" value={primarySource.object_type} onChange={(event) => updateSource('object_type', event.target.value)}>{OBJECT_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
                <label className="text-sm">Owner property<input className="mt-1 w-full border rounded-lg px-3 py-2" value={primarySource.owner_property_name} onChange={(event) => updateSource('owner_property_name', event.target.value)} /></label>
                <label className="text-sm">Refresh strategy<select className="mt-1 w-full border rounded-lg px-3 py-2" value={primarySource.execution_mode} onChange={(event) => updateSource('execution_mode', event.target.value)}><option value="scheduled">Scheduled</option><option value="real_time">Live lookup</option></select></label>
                {primarySource.execution_mode === 'scheduled' && <label className="text-sm">Refresh every<select className="mt-1 w-full border rounded-lg px-3 py-2" value={primarySource.sync_frequency_minutes || 15} onChange={(event) => updateSource('sync_frequency_minutes', Number(event.target.value))}><option value="5">5 minutes</option><option value="15">15 minutes</option><option value="30">30 minutes</option><option value="60">60 minutes</option></select></label>}
              </div>
              <p className="text-xs text-gray-500">HubSpot uses the criteria from step 3 automatically. Each value gets its own workload query and independent limit. The cap period controls the counter bucket; it does not filter records by assignment date.</p>
              {form.preserve_external_conditions && <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                This existing cap has advanced HubSpot-only filters. They will be preserved so behavior does not change.
                <label className="block mt-2"><input type="checkbox" className="mr-2" checked={!form.preserve_external_conditions} onChange={(event) => updateForm('preserve_external_conditions', !event.target.checked)} />Replace them with the shared criteria from step 3</label>
              </div>}
            </div>}
            </section>

            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4"><h3 className="font-semibold text-blue-900 mb-1">Review</h3><p className="text-sm text-blue-800">Cap {ownerNames.get(String(form.hs_owner_id)) || 'the selected member'} with {form.segments.length} {form.segments.length === 1 ? 'limit' : 'condition-specific limits'} per {form.period_type === 'none' ? 'non-resetting period' : form.period_type}, {form.scope_type === 'team_member' ? `within ${teamNames.get(String(form.hs_team_id)) || 'the selected team'}` : 'across all teams'}. Use {USAGE_SOURCE_LABELS[form.usage_source]?.toLowerCase()}.</p></section>
            <label className="text-sm"><input type="checkbox" className="mr-2" checked={form.is_active} onChange={(event) => updateForm('is_active', event.target.checked)} />Active</label>
            {formError && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700">{formError}</div>}
          </div>
          <div className="px-6 py-4 border-t flex justify-end gap-3"><button className="px-4 py-2 border rounded-lg" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button><button className="px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save cap'}</button></div>
        </div>
      </div>}
    </div>
  );
}
